/**
 * Super app (Click, Payme, Zoomrad, …) UI tokenlari.
 * `globals.css` dagi `var(--brand-*, fallback)` bilan bog’langan —
 * applyBrandTokens() document root ga haqiqiy qiymatlarni yozadi.
 *
 * Ranglar:  `--brand-bg-*`, `--brand-text-*`, `--brand-stroke-*`, `--brand-icon-*`
 * Click+:   `--click-bg-*`, `--click-text-*`, `--click-icon-*`, `--click-stroke-*`,
 *           `--click-button-*`, `--click-tag-*`, `--click-progress-*`
 * Tipografiya: `--brand-font-family`, `--brand-font-family-heading`,
 *              `--brand-text-xs` … `--brand-text-3xl`
 * `document.documentElement` da `data-brand` atributi o’rnatiladi.
 *
 * globals.css `@theme inline` bloki shu CSS var larni Tailwind utility
 * classlariga ulaydi — `text-sm`, `text-base` va h.k. brand konfigiga bog’liq.
 */

import { detectOwnerApp } from "@/lib/env";
import { getActiveBrandId } from "@/hosts";

/**
 * `var(--click-*-*, …)` — faqat default (Inson Uchun) brand uchun fallback.
 * Figma redesign: qattiq orange, tekis (gradient yo'q). Kalit nomlari
 * (`ctaGradient` va h.k.) back-compat uchun saqlangan, qiymatlar solid.
 */
export const CLICK_STYLE_FALLBACK = {
  ctaGradient: "#f25822",
  progressGradient: "#f25822",
  accentSolid: "#f25822",
  onCta: "#ffffff",
} as const;

export type ThemeMode = "light" | "dark";

/** Integratorlar alohida token set bilan ulashishi mumkin bo‘lgan brand kalitlari */
export type BrandId = "default" | "click" | "payme" | "zoomrad" | "paynet" | "anorbank";

/* ────────────────────────────────────────────────────────────────────────────
 * Typography tokens
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Har bir brand o’z shrift oilasi va font o’lchamlar jadvalini taqdim etishi
 * mumkin. `applyBrandTokens()` bularni `--brand-font-*` va `--brand-text-*`
 * CSS o’zgaruvchilari sifatida root ga yozadi.
 *
 * globals.css `@theme inline` esa shu o’zgaruvchilarni Tailwind `text-*`
 * utility classlariga va `font-sans` classiga ulaydi — demak komponentlarda
 * hech narsa o’zgarmaydi, brand config o’zgarsa CSS var orqali qiymat yangilanadi.
 *
 * Mavjud brandlar uchun qiymatlar Tailwind v4 default lari bilan mos —
 * hozirgi vizual ko’rinish o’zgarmaydi.
 */

/**
 * Shrift oilasi konfiguratsiyasi. `sans` majburiy, `heading` ixtiyoriy
 * (yo’q bo’lsa heading lar ham `sans` ishlatadi).
 * Super app o’z webviewiga kerakli shrift fayllarni mustaqil yuklab qo’yishi kerak.
 */
export interface FontFamilyTokens {
  sans: string;
  heading?: string;
}

/**
 * Shrift o’lchamlar jadvali — rem birligida.
 * Tailwind v4 dagi `text-xs` … `text-3xl` classlariga mos keladi.
 */
export interface FontSizeTokens {
  xs: string; // text-xs  → 0.75rem  (12px)
  sm: string; // text-sm  → 0.875rem (14px)
  base: string; // text-base → 1rem    (16px)
  lg: string; // text-lg  → 1.125rem (18px)
  xl: string; // text-xl  → 1.25rem  (20px)
  "2xl": string; // text-2xl → 1.5rem   (24px)
  "3xl": string; // text-3xl → 1.875rem (30px)
}

export interface TypographyTokens {
  fontFamily: FontFamilyTokens;
  fontSize: FontSizeTokens;
}

/** Tailwind v4 default lari bilan mos — hech nima o’zgarmaydi. Boshqa brandlar extend qilishi uchun export qilingan. */
export const DEFAULT_TYPOGRAPHY: TypographyTokens = {
  fontFamily: {
    sans: "Inter, ui-sans-serif, system-ui, sans-serif",
  },
  fontSize: {
    xs: "0.75rem",
    sm: "0.875rem",
    base: "1rem",
    lg: "1.125rem",
    xl: "1.25rem",
    "2xl": "1.5rem",
    "3xl": "1.875rem",
  },
};

/** Zoomrad super app tipografiyasi — Montserrat shrift oilasi */
export const ZOOMRAD_TYPOGRAPHY: TypographyTokens = {
  fontFamily: {
    sans: "Montserrat, ui-sans-serif, system-ui, sans-serif",
  },
  fontSize: {
    xs: "0.75rem",
    sm: "0.875rem",
    base: "1rem",
    lg: "1.125rem",
    xl: "1.25rem",
    "2xl": "1.5rem",
    "3xl": "1.875rem",
  },
};

/** Bir tema (light yoki dark) uchun barcha brand CSS o’zgaruvchilari */
export interface BrandThemeTokens {
  bgPrimary: string;
  bgSecondary: string;
  bgAccent: string;
  textPrimary: string;
  textSecondary: string;
  textInverse: string;
  textDisabled: string;
  textDanger: string;
  strokeSecondary: string;
  strokePrimary: string;
  strokeAccent: string;
  iconAccent: string;
  progressFill: string;
  progressTrack: string;
  tagAccentBg: string;
  tagAccentFg: string;
  tagAccentShadow: string;
}

export interface BrandTokenSet {
  light: BrandThemeTokens;
  dark: BrandThemeTokens;
}

/**
 * Click super app — Figma «Семантические цвета» (yuklangan spec / SVG bilan mos).
 * 8 xonali hex (#RRGGBBAA) — alpha Figma dagiidek.
 */
export interface ClickSemanticTokens {
  bg: {
    ocean: string;
    island: string;
    accent: string;
    disabled: string;
    natural: string;
    glass: string;
    positive: string;
    neutral: string;
    attention: string;
    negative: string;
    backdrop: string;
    transparent: string;
  };
  text: {
    primary: string;
    secondary: string;
    accent: string;
    disabled: string;
    tertiary: string;
    onAccent: string;
    onDisabled: string;
    error: string;
    onLightAccent: string;
  };
  icon: {
    accent: string;
    primary: string;
    disabled: string;
    onAccent: string;
    onDisabled: string;
    secondary: string;
    onLightAccent: string;
    error: string;
  };
  stroke: {
    primary: string;
    accent: string;
    error: string;
    secondary: string;
    disabled: string;
  };
  /** Asosiy CTA / filled button */
  button: {
    primaryBg: string;
    primaryFg: string;
  };
  /** Filter tab, segment, ariza chip — yashil gradient emas, bir xil primary */
  tag: {
    accentBg: string;
    accentFg: string;
    accentShadow: string;
  };
  /** Progress: to‘ldirilgan / track */
  progress: {
    fill: string;
    track: string;
  };
}

/**
 * Light / Dark — Click dizayn tizimi.
 * `light` bloki faqat `data-theme="light"` va `useBrandTokens("light")` bilan qo‘llanadi
 * (Click super app `click-theme` cookie yoki profildan yorug‘lik rejimi).
 */
export const CLICK_SEMANTIC: Record<ThemeMode, ClickSemanticTokens> = {
  light: {
    bg: {
      ocean: "#F4F5F5",
      island: "#FFFFFF",
      accent: "#0077FF",
      disabled: "#F2F4F5",
      natural: "#D5DDE5",
      glass: "#EBEEF080",
      positive: "#00CC52",
      neutral: "#8B8E8F",
      attention: "#FF8300",
      negative: "#FF4400",
      backdrop: "#0000004D",
      transparent: "#F4F4F452",
    },
    text: {
      primary: "#141414",
      secondary: "#7E8286",
      accent: "#0077FF",
      disabled: "#B2B6B9",
      tertiary: "#A0A5A9",
      onAccent: "#FFFFFF",
      onDisabled: "#FFFFFF",
      error: "#FF4400",
      onLightAccent: "#141414",
    },
    icon: {
      accent: "#0077FF",
      primary: "#7E8286",
      disabled: "#B2B6B9",
      onAccent: "#FFFFFF",
      onDisabled: "#FFFFFF",
      secondary: "#141414",
      onLightAccent: "#141414",
      error: "#FF4400",
    },
    stroke: {
      primary: "#E4E5E6",
      accent: "#0077FF",
      error: "#FF4400",
      secondary: "#7E8286",
      disabled: "#D5DDE5",
    },
    button: {
      primaryBg: "#0077FF",
      primaryFg: "#FFFFFF",
    },
    tag: {
      accentBg: "#0077FF",
      accentFg: "#FFFFFF",
      accentShadow: "0 2px 12px rgba(0, 119, 255, 0.35)",
    },
    progress: {
      fill: "#0077FF",
      track: "#C5D2E0",
    },
  },
  dark: {
    bg: {
      ocean: "#141414",
      island: "#222222",
      accent: "#0077FF",
      disabled: "#363636",
      natural: "#31393E",
      glass: "#3F3E3F80",
      positive: "#00CC52",
      neutral: "#5F5E5F",
      attention: "#FF8300",
      negative: "#FF4400",
      backdrop: "#11111180",
      transparent: "#4B4B4B52",
    },
    text: {
      primary: "#FFFFFF",
      secondary: "#99A0A3",
      accent: "#0077FF",
      disabled: "#535658",
      tertiary: "#4C5052",
      onAccent: "#FFFFFF",
      onDisabled: "#141414",
      error: "#FF4400",
      onLightAccent: "#141414",
    },
    icon: {
      accent: "#0077FF",
      primary: "#99A0A3",
      disabled: "#535658",
      onAccent: "#FFFFFF",
      onDisabled: "#141414",
      secondary: "#FFFFFF",
      onLightAccent: "#141414",
      error: "#FF4400",
    },
    stroke: {
      primary: "#1F1F1F",
      accent: "#0077FF",
      error: "#FF4400",
      secondary: "#000000",
      disabled: "#31393E",
    },
    button: {
      primaryBg: "#0077FF",
      primaryFg: "#FFFFFF",
    },
    tag: {
      accentBg: "#0077FF",
      accentFg: "#FFFFFF",
      accentShadow: "0 2px 12px rgba(0, 119, 255, 0.45)",
    },
    progress: {
      fill: "#0077FF",
      track: "#C5D2E0",
    },
  },
};
export const ZOOMRAD_SEMANTIC: Record<ThemeMode, ClickSemanticTokens> = {
  light: {
    bg: {
      ocean: "#FBFAFB",       // bg/primary
      island: "#F8FAFB",      // bg/secondary
      accent: "#20B575",      // brand/default
      disabled: "#EDF1F1",    // brand/subtle (disabled surface)
      natural: "#DCE2EA",     // border/default (neutral surface)
      glass: "#FBFAFB80",
      positive: "#20A34A",    // success/default
      neutral: "#9FB2AE",     // border/strong
      attention: "#F6AD06",   // warning/default
      negative: "#E53B3E",    // error/default
      backdrop: "#0D111780",
      transparent: "#FBFAFB52",
    },
    text: {
      primary: "#0D1117",     // text/primary
      secondary: "#475361",   // text/secondary
      accent: "#20B575",      // brand/default
      disabled: "#C4CDDE",    // text/disabled
      tertiary: "#697A8E",    // text/tertiary
      onAccent: "#FFFFFF",    // brand/on-brand
      onDisabled: "#FFFFFF",
      error: "#E53B3E",       // error/default
      onLightAccent: "#0D1117",
    },
    icon: {
      accent: "#00B079",      // icon/brand
      primary: "#0D1117",     // icon/primary
      disabled: "#C4CDDE",    // icon/disabled
      onAccent: "#FFFFFF",
      onDisabled: "#FFFFFF",
      secondary: "#675B80",   // icon/secondary
      onLightAccent: "#0D1117",
      error: "#E53B3E",
    },
    stroke: {
      primary: "#DCE2EA",     // border/default
      accent: "#00B079",      // border/brand
      error: "#E53B3E",
      secondary: "#9FB2AE",   // border/strong
      disabled: "#EDF1F1",
    },
    button: {
      primaryBg: "#20B575",   // brand/default
      primaryFg: "#FFFFFF",
    },
    tag: {
      accentBg: "#20B575",
      accentFg: "#FFFFFF",
      accentShadow: "0 2px 12px rgba(32, 181, 117, 0.35)",
    },
  progress: {
      fill: "#16A34A",
      track: "#DCE2EA",
    },
  },

  dark: {
    bg: {
      ocean: "#0D1117",       // bg/primary
      island: "#1A1F1E",      // bg/secondary
      accent: "#39B488",      // brand/default (dark)
      disabled: "#2D3A40",    // brand/subtle (dark)
      natural: "#1F2F25",     // bg/tertiary
      glass: "#1A1F1E80",
      positive: "#16A34A",    // success/default (dark)
      neutral: "#496157",     // border/strong (dark)
      attention: "#F6AD06",   // warning/default
      negative: "#EF5B5E",    // error/default (dark)
      backdrop: "#0D111780",
      transparent: "#2D3A4052",
    },
    text: {
      primary: "#FFFFFF",     // text/primary
      secondary: "#9FB2A2",   // text/secondary
      accent: "#4DC78D",      // brand (dark accent)
      disabled: "#203A40",    // text/disabled
      tertiary: "#607B60",    // text/tertiary
      onAccent: "#F5FFFF",    // brand/on-brand (dark)
      onDisabled: "#0D1117",
      error: "#EF5B5E",       // error/default (dark)
      onLightAccent: "#0D1117",
    },
    icon: {
      accent: "#20BA49",      // icon/brand (dark)
      primary: "#FFFFFF",     // icon/primary
      disabled: "#203A40",    // icon/disabled
      onAccent: "#FFFFFF",
      onDisabled: "#0D1117",
      secondary: "#9FB2A2",   // icon/secondary
      onLightAccent: "#0D1117",
      error: "#EF5B5E",
    },
    stroke: {
      primary: "#20354D",     // border/default (dark)
      accent: "#00B079",      // border/brand
      error: "#EF5B5E",
      secondary: "#496157",   // border/strong (dark)
      disabled: "#1F2D29",
    },
    button: {
      primaryBg: "#16A34A",   // brand/default (dark)
      primaryFg: "#FFFFFF",
    },
    tag: {
      accentBg: "#39B488",
      accentFg: "#FFFFFF",
      accentShadow: "0 2px 12px rgba(57, 180, 136, 0.45)",
    },
    progress: {
      fill: "#16A34A",
      track: "#1F2D29",
    },
  },
};

/**
 * Payme super app — Figma «Semantics» spec (Brand/Primary, Text, Background,
 * Border, Icon, Success, Error, Warning, Info).
 * `ClickSemanticTokens` strukturasiga map qilinadi — umumiy mapper ishlatiladi.
 */
export const PAYME_SEMANTIC: Record<ThemeMode, ClickSemanticTokens> = {
  light: {
    bg: {
      ocean: "#FBFAFB",
      island: "#F0F0F8",
      accent: "#20B575",
      disabled: "#F0F0F8",
      natural: "#DCE2EA",
      glass: "#FBFAFB80",
      positive: "#20A34A",
      neutral: "#9FB2AE",
      attention: "#F6AD06",
      negative: "#E53B3E",
      backdrop: "#0D111780",
      transparent: "#FBFAFB52",
    },
    text: {
      primary: "#0D1117",
      secondary: "#475361",
      accent: "#20B575",
      disabled: "#C4CDDE",
      tertiary: "#697A8E",
      onAccent: "#FFFFFF",
      onDisabled: "#FFFFFF",
      error: "#E53B3E",
      onLightAccent: "#0D1117",
    },
    icon: {
      accent: "#00B079",
      primary: "#0D1117",
      disabled: "#C4CDDE",
      onAccent: "#FFFFFF",
      onDisabled: "#FFFFFF",
      secondary: "#675B80",
      onLightAccent: "#0D1117",
      error: "#E53B3E",
    },
    stroke: {
      primary: "#DCE2EA",
      accent: "#00B079",
      error: "#E53B3E",
      secondary: "#9FB2AE",
      disabled: "#DCE2EA",
    },
    button: {
      primaryBg: "#20B575",
      primaryFg: "#FFFFFF",
    },
    tag: {
      accentBg: "#20B575",
      accentFg: "#FFFFFF",
      accentShadow: "0 2px 12px rgba(32, 181, 117, 0.35)",
    },
    progress: {
      fill: "#20B575",
      track: "#DCE2EA",
    },
  },
  dark: {
    bg: {
      ocean: "#0D1117",
      island: "#1A1F1E",
      accent: "#20B575",
      disabled: "#2D3A40",
      natural: "#1F2F25",
      glass: "#1A1F1E80",
      positive: "#168534",
      neutral: "#496157",
      attention: "#F6AD06",
      negative: "#E53B3E",
      backdrop: "#0D111780",
      transparent: "#2D3A4052",
    },
    text: {
      primary: "#FFFFFF",
      secondary: "#9FB2A2",
      accent: "#4DC78D",
      disabled: "#203A40",
      tertiary: "#607B60",
      onAccent: "#FFFFFF",
      onDisabled: "#0D1117",
      error: "#E53B3E",
      onLightAccent: "#0D1117",
    },
    icon: {
      accent: "#20BA49",
      primary: "#FFFFFF",
      disabled: "#203A40",
      onAccent: "#FFFFFF",
      onDisabled: "#0D1117",
      secondary: "#9FB2A2",
      onLightAccent: "#0D1117",
      error: "#E53B3E",
    },
    stroke: {
      primary: "#20354D",
      accent: "#00B079",
      error: "#E53B3E",
      secondary: "#496157",
      disabled: "#1F2D29",
    },
    button: {
      primaryBg: "#20B575",
      primaryFg: "#FFFFFF",
    },
    tag: {
      accentBg: "#20B575",
      accentFg: "#FFFFFF",
      accentShadow: "0 2px 12px rgba(32, 181, 117, 0.45)",
    },
    progress: {
      fill: "#20B575",
      track: "#1F2D29",
    },
  },
};

/** Figma semantic → mini app `--brand-*` (globals.css) mapping */
function semanticToBrandTokens(s: ClickSemanticTokens): BrandThemeTokens {
  return {
    bgPrimary: s.bg.ocean,
    bgSecondary: s.bg.island,
    bgAccent: s.bg.accent,
    textPrimary: s.text.primary,
    textSecondary: s.text.secondary,
    textInverse: s.text.onAccent,
    textDisabled: s.text.disabled,
    textDanger: s.text.error,
    strokePrimary: s.stroke.primary,
    strokeSecondary: s.stroke.secondary,
    strokeAccent: s.stroke.accent,
    iconAccent: s.icon.accent,
    progressFill: s.progress.fill,
    progressTrack: s.progress.track,
    tagAccentBg: s.tag.accentBg,
    tagAccentFg: s.tag.accentFg,
    tagAccentShadow: s.tag.accentShadow,
  };
}

/** Click integratsiyasi — `applyBrandTokens` orqali root ga yoziladi */
const CLICK_BRAND_TOKEN_SET: BrandTokenSet = {
  dark: semanticToBrandTokens(CLICK_SEMANTIC.dark),
  light: semanticToBrandTokens(CLICK_SEMANTIC.light),
};

/** Zoomrad integratsiyasi — `applyBrandTokens` orqali root ga yoziladi */
const ZOOMRAD_BRAND_TOKEN_SET: BrandTokenSet = {
  dark: semanticToBrandTokens(ZOOMRAD_SEMANTIC.dark),
  light: semanticToBrandTokens(ZOOMRAD_SEMANTIC.light),
};
export const PAYMEE_BRAND_TOKEN_SET = {
  dark: semanticToBrandTokens(PAYME_SEMANTIC.dark),
  light: semanticToBrandTokens(PAYME_SEMANTIC.light),
};

/**
 * Paynet super app — turkuaz/teal brend palitrasi.
 *
 * PROVISIONAL: Paynet rasmiy Figma «Semantics» spec'i kelgach shu qiymatlar
 * yangilanadi. Hozircha kontrast xavfsiz bo'lishi uchun Tailwind teal shkalasidan
 * olindi (oq matn teal-600 ustida AA-large kontrastdan o'tadi). Paynet'ga
 * qo'shimcha `--paynet-*` CSS vars kerak emas (Payme kabi) — shuning uchun
 * `applyExtraVars` yo'q, faqat `--brand-*` token'lari yoziladi.
 */
const PAYNET_BRAND_TOKEN_SET: BrandTokenSet = {
  dark: {
    bgPrimary: "#0A1413",
    bgSecondary: "#141F1E",
    bgAccent: "#14B8A6",
    textPrimary: "#F2F8F7",
    textSecondary: "#9CB3B0",
    textInverse: "#FFFFFF",
    textDisabled: "rgba(242, 248, 247, 0.3)",
    textDanger: "#EF5B5E",
    strokeSecondary: "#1E2C2A",
    strokePrimary: "#243532",
    strokeAccent: "#14B8A6",
    iconAccent: "#2DD4BF",
    progressFill: "#14B8A6",
    progressTrack: "#1E2C2A",
    tagAccentBg: "#0D9488",
    tagAccentFg: "#FFFFFF",
    tagAccentShadow: "0 2px 12px rgba(20, 184, 166, 0.45)",
  },
  light: {
    bgPrimary: "#F3F6F6",
    bgSecondary: "#FFFFFF",
    bgAccent: "#0D9488",
    textPrimary: "#0B1D1B",
    textSecondary: "#5A716E",
    textInverse: "#FFFFFF",
    textDisabled: "rgba(11, 29, 27, 0.4)",
    textDanger: "#E5484D",
    strokeSecondary: "#EAF0EF",
    strokePrimary: "#DBE5E3",
    strokeAccent: "#0D9488",
    iconAccent: "#0D9488",
    progressFill: "#0D9488",
    progressTrack: "#D2E3E0",
    tagAccentBg: "#0D9488",
    tagAccentFg: "#FFFFFF",
    tagAccentShadow: "0 2px 12px rgba(13, 148, 136, 0.35)",
  },
};

/**
 * AnorBank super app — "anor" (pomegranate) qizil palitrasi.
 *
 * PROVISIONAL: AnorBank rasmiy Figma «Semantics» spec'i kelgach shu qiymatlar
 * yangilanadi. Hozircha kontrast xavfsiz bo'lishi uchun Tailwind rose/red
 * shkalasidan olindi. Paynet kabi qo'shimcha `--anorbank-*` CSS vars kerak
 * emas — faqat `--brand-*` token'lari yoziladi.
 */
const ANORBANK_BRAND_TOKEN_SET: BrandTokenSet = {
  dark: {
    bgPrimary: "#160A0B",
    bgSecondary: "#211416",
    bgAccent: "#E11D48",
    textPrimary: "#FAF2F3",
    textSecondary: "#C2A0A4",
    textInverse: "#FFFFFF",
    textDisabled: "rgba(250, 242, 243, 0.3)",
    textDanger: "#EF5B5E",
    strokeSecondary: "#2C1A1C",
    strokePrimary: "#37282A",
    strokeAccent: "#E11D48",
    iconAccent: "#FB7185",
    progressFill: "#E11D48",
    progressTrack: "#2C1A1C",
    tagAccentBg: "#BE123C",
    tagAccentFg: "#FFFFFF",
    tagAccentShadow: "0 2px 12px rgba(225, 29, 72, 0.45)",
  },
  light: {
    bgPrimary: "#FBF3F4",
    bgSecondary: "#FFFFFF",
    bgAccent: "#BE123C",
    textPrimary: "#210B0F",
    textSecondary: "#7A5A5E",
    textInverse: "#FFFFFF",
    textDisabled: "rgba(33, 11, 15, 0.4)",
    textDanger: "#E5484D",
    strokeSecondary: "#F5E7E8",
    strokePrimary: "#EAD5D7",
    strokeAccent: "#BE123C",
    iconAccent: "#BE123C",
    progressFill: "#BE123C",
    progressTrack: "#EAD5D7",
    tagAccentBg: "#BE123C",
    tagAccentFg: "#FFFFFF",
    tagAccentShadow: "0 2px 12px rgba(190, 18, 60, 0.35)",
  },
};

/**
 * Ilova default palitrasi — "Inson Uchun" brendi (Figma redesign, orange).
 * `globals.css` dagi fallback lar bilan mos. Progress/teglar Figma bo'yicha
 * QATTIQ orange (gradient emas) va TEKIS (shadow yo'q).
 */
const DEFAULT_BRAND_TOKEN_SET: BrandTokenSet = {
  dark: {
    bgPrimary: "#000000",
    bgSecondary: "#18181b",
    bgAccent: "#f25822",
    textPrimary: "#f8f8f7",
    textSecondary: "#c1beb3",
    textInverse: "#ffffff",
    textDisabled: "rgba(248, 248, 247, 0.3)",
    textDanger: "#f2415a",
    strokeSecondary: "#26231e",
    strokePrimary: "#2e2a24",
    strokeAccent: "#f25822",
    iconAccent: "#f25822",
    progressFill: "#f25822",
    progressTrack: "#26231e",
    tagAccentBg: "#f25822",
    tagAccentFg: "#ffffff",
    tagAccentShadow: "none",
  },
  light: {
    bgPrimary: "#f4f4f6",
    bgSecondary: "#ffffff",
    bgAccent: "#f25822",
    textPrimary: "#09093f",
    textSecondary: "#6b6b8c",
    textInverse: "#ffffff",
    textDisabled: "rgba(9, 9, 63, 0.5)",
    textDanger: "#ee203b",
    strokeSecondary: "#f3f3f5",
    strokePrimary: "#ebe9e9",
    strokeAccent: "#f25822",
    iconAccent: "#f25822",
    progressFill: "#f25822",
    progressTrack: "#f3f3f5",
    tagAccentBg: "#f25822",
    tagAccentFg: "#ffffff",
    tagAccentShadow: "none",
  },
};

/* ────────────────────────────────────────────────────────────────────────────
 * Brand registry
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Har bir brand `BrandDefinition` orqali ifodalanadi:
 *   - `tokens`      — `--brand-*` rang CSS o'zgaruvchilari uchun light/dark to'plami
 *   - `typography`  — `--brand-font-*` va `--brand-text-*` CSS o'zgaruvchilari
 *   - `applyExtraVars` — brand-specific qo'shimcha CSS vars (mas. Click `--click-*`)
 *
 * Yangi super app qo'shish:
 *   1. rang spec ni const sifatida yozing (mas. `PAYME_SEMANTIC`)
 *   2. `BrandThemeTokens` ga map qiling
 *   3. kerak bo'lsa alohida `TypographyTokens` yarating (yoki `DEFAULT_TYPOGRAPHY` extend qiling)
 *   4. `BRANDS` ga yangi yozuv qo'shing
 *   5. `BrandId` type va `src/hosts/<vendor>Host.ts` ga `brandId` ni mos qiling
 */

interface BrandDefinition {
  id: BrandId;
  tokens: BrandTokenSet;
  /** Shrift oilasi va font o'lchamlar jadvali */
  typography: TypographyTokens;
  /** Brand-specific qo'shimcha CSS o'zgaruvchilari (mas. `--click-text-error`) */
  applyExtraVars?: (mode: ThemeMode) => void;
}

function setVar(name: string, value: string) {
  if (typeof document === "undefined") return;
  document.documentElement.style.setProperty(name, value);
}

function camelToKebab(key: string): string {
  return key.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
}

/** Bir guruh ob'ektni `--<prefix>-<group>-<key>` shaklida CSS variable ga yozadi */
function applyGroupVars(
  prefix: string,
  groupName: string,
  obj: Record<string, string>,
) {
  for (const [key, val] of Object.entries(obj)) {
    setVar(`--${prefix}-${groupName}-${camelToKebab(key)}`, val);
  }
}

/** Figma nomlari bilan qo'shimcha CSS o'zgaruvchilar (mas. `var(--click-text-error)`) */
function applyClickFigmaCssVars(mode: ThemeMode) {
  const s = CLICK_SEMANTIC[mode];
  applyGroupVars("click", "bg", s.bg as Record<string, string>);
  applyGroupVars("click", "text", s.text as Record<string, string>);
  applyGroupVars("click", "icon", s.icon as Record<string, string>);
  applyGroupVars("click", "stroke", s.stroke as Record<string, string>);
  applyGroupVars("click", "button", s.button as Record<string, string>);
  applyGroupVars("click", "tag", s.tag as Record<string, string>);
  applyGroupVars("click", "progress", s.progress as Record<string, string>);
}

function applyZoomradFigmaCssVars(mode: ThemeMode) {
  const s = ZOOMRAD_SEMANTIC[mode];
  applyGroupVars("zoomrad", "bg", s.bg as Record<string, string>);
  applyGroupVars("zoomrad", "text", s.text as Record<string, string>);
  applyGroupVars("zoomrad", "icon", s.icon as Record<string, string>);
  applyGroupVars("zoomrad", "stroke", s.stroke as Record<string, string>);
  applyGroupVars("zoomrad", "button", s.button as Record<string, string>);
  applyGroupVars("zoomrad", "tag", s.tag as Record<string, string>);
  applyGroupVars("zoomrad", "progress", s.progress as Record<string, string>);
}

const BRANDS: Record<BrandId, BrandDefinition> = {
  default: {
    id: "default",
    tokens: DEFAULT_BRAND_TOKEN_SET,
    typography: DEFAULT_TYPOGRAPHY,
  },
  click: {
    id: "click",
    tokens: CLICK_BRAND_TOKEN_SET,
    typography: DEFAULT_TYPOGRAPHY,
    applyExtraVars: applyClickFigmaCssVars,
  },
  payme: {
    id: "payme",
    tokens: PAYMEE_BRAND_TOKEN_SET,
    typography: DEFAULT_TYPOGRAPHY,
  },
  zoomrad: {
    id: "zoomrad",
    tokens: ZOOMRAD_BRAND_TOKEN_SET,
    typography: ZOOMRAD_TYPOGRAPHY,
    applyExtraVars: applyZoomradFigmaCssVars,
  },
  paynet: {
    id: "paynet",
    tokens: PAYNET_BRAND_TOKEN_SET,
    typography: DEFAULT_TYPOGRAPHY,
  },
  anorbank: {
    id: "anorbank",
    tokens: ANORBANK_BRAND_TOKEN_SET,
    typography: DEFAULT_TYPOGRAPHY,
  },
};

/** Back-compat: ba'zi joylarda hozircha `BRAND_TOKENS[brand][mode]` deb foydalanilishi mumkin */
export const BRAND_TOKENS: Record<BrandId, BrandTokenSet> = {
  default: BRANDS.default.tokens,
  click: BRANDS.click.tokens,
  payme: BRANDS.payme.tokens,
  zoomrad: BRANDS.zoomrad.tokens,
  paynet: BRANDS.paynet.tokens,
  anorbank: BRANDS.anorbank.tokens,
};

const ALL_BRAND_IDS: readonly BrandId[] = [
  "default",
  "click",
  "payme",
  "zoomrad",
  "paynet",
  "anorbank",
];

/** Dev: `?brand=click` (yoki payme, zoomrad) — localhostda Click palitrasini ko'rish */
function resolveBrandIdFromDevQuery(): BrandId | null {
  if (!import.meta.env.DEV || typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search)
    .get("brand")
    ?.toLowerCase();
  return (ALL_BRAND_IDS as readonly string[]).includes(raw ?? "")
    ? (raw as BrandId)
    : null;
}

/**
 * Runtime da qaysi brand tokenlari ishlatilishini aniqlaydi.
 * Kanonik manba — aktiv host (`@/hosts`). Dev query param va `detectOwnerApp`
 * fallback sifatida qoldirildi.
 */
export function resolveBrandId(): BrandId {
  const fromQuery = resolveBrandIdFromDevQuery();
  if (fromQuery) return fromQuery;

  // Host registry birinchi navbatda — Click/Zoomrad/Payme ga to'g'ri yo'naltiradi.
  const fromHost = getActiveBrandId();
  if (fromHost && BRANDS[fromHost]) return fromHost;

  // Legacy fallback — registry hali resolve bo'lmagan paytlarda
  return detectOwnerApp() === "click" ? "click" : "default";
}

/** Root elementga `--brand-*` (va brand-specific qo'shimcha) CSS o'zgaruvchilarini yozadi */
export function applyBrandTokens(brand: BrandId, mode: ThemeMode) {
  if (typeof document === "undefined") return;

  const def = BRANDS[brand] ?? BRANDS.default;

  document.documentElement.setAttribute("data-brand", def.id);

  const t = def.tokens[mode];

  setVar("--brand-bg-primary", t.bgPrimary);
  setVar("--brand-bg-secondary", t.bgSecondary);
  setVar("--brand-bg-accent", t.bgAccent);
  setVar("--brand-text-primary", t.textPrimary);
  setVar("--brand-text-secondary", t.textSecondary);
  setVar("--brand-text-inverse", t.textInverse);
  setVar("--brand-text-disabled", t.textDisabled);
  setVar("--brand-text-danger", t.textDanger);
  setVar("--brand-stroke-secondary", t.strokeSecondary);
  setVar("--brand-stroke-primary", t.strokePrimary);
  setVar("--brand-stroke-accent", t.strokeAccent);
  setVar("--brand-icon-accent", t.iconAccent);
  setVar("--brand-progress-fill", t.progressFill);
  setVar("--brand-progress-track", t.progressTrack);
  setVar("--brand-tag-accent-bg", t.tagAccentBg);
  setVar("--brand-tag-accent-fg", t.tagAccentFg);
  setVar("--brand-tag-accent-shadow", t.tagAccentShadow);

  // Typography — tema ga bog'liq emas, shuning uchun theme parametri ishlatilmaydi
  const ty = def.typography;
  setVar("--brand-font-family", ty.fontFamily.sans);
  if (ty.fontFamily.heading) {
    setVar("--brand-font-family-heading", ty.fontFamily.heading);
  }
  setVar("--brand-text-xs", ty.fontSize.xs);
  setVar("--brand-text-sm", ty.fontSize.sm);
  setVar("--brand-text-base", ty.fontSize.base);
  setVar("--brand-text-lg", ty.fontSize.lg);
  setVar("--brand-text-xl", ty.fontSize.xl);
  setVar("--brand-text-2xl", ty.fontSize["2xl"]);
  setVar("--brand-text-3xl", ty.fontSize["3xl"]);

  def.applyExtraVars?.(mode);
}
